import { useState, useRef, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "motion/react";
import { Link, useLocation } from "react-router-dom";
import { blogPosts } from "../data/blog";
import { ArrowUpRight, ArrowRight, ArrowLeft, Clock, Calendar, MoveHorizontal } from "lucide-react";

export default function BlogPage() {
  const location = useLocation();
  const isHome = location.pathname === "/";
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState<"left" | "right">("right");

  const total = blogPosts.length;

  const next = useCallback(() => {
    setDirection("right");
    setCurrentIndex((prev) => (prev + 1) % total);
  }, [total]);

  const prev = useCallback(() => {
    setDirection("left");
    setCurrentIndex((prev) => (prev - 1 + total) % total);
  }, [total]);

  // Trackpad horizontal 2-finger scroll listener (laptop / desktop)
  const wheelLockRef = useRef(false);
  const wheelAccumulatorRef = useRef(0);
  const wheelTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const sectionEl = document.getElementById("journal-section");
    if (!sectionEl) return;

    const handleWheel = (e: WheelEvent) => {
      if (Math.abs(e.deltaX) > 0 && Math.abs(e.deltaX) >= Math.abs(e.deltaY)) {
        e.preventDefault();
        if (wheelLockRef.current) return;

        wheelAccumulatorRef.current += e.deltaX;

        if (Math.abs(wheelAccumulatorRef.current) > 35) {
          if (wheelAccumulatorRef.current > 0) {
            next();
          } else {
            prev();
          }
          wheelLockRef.current = true;
          wheelAccumulatorRef.current = 0;

          if (wheelTimerRef.current) clearTimeout(wheelTimerRef.current);
          wheelTimerRef.current = setTimeout(() => {
            wheelLockRef.current = false;
            wheelAccumulatorRef.current = 0;
          }, 650);
        }
      } else {
        wheelAccumulatorRef.current = 0;
      }
    };

    sectionEl.addEventListener("wheel", handleWheel, { passive: false });
    return () => {
      sectionEl.removeEventListener("wheel", handleWheel);
      if (wheelTimerRef.current) clearTimeout(wheelTimerRef.current);
    };
  }, [next, prev]);

  // Touch handlers for mobile finger swipes
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);
  const isSwipingRef = useRef(false);
  const touchLockRef = useRef(false);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartRef.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
    };
    isSwipingRef.current = false;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!touchStartRef.current) return;
    const deltaX = touchStartRef.current.x - e.touches[0].clientX;
    const deltaY = touchStartRef.current.y - e.touches[0].clientY;

    if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 10) {
      isSwipingRef.current = true;
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (!touchStartRef.current || touchLockRef.current) {
      touchStartRef.current = null;
      return;
    }
    const deltaX = touchStartRef.current.x - e.changedTouches[0].clientX;
    const deltaY = touchStartRef.current.y - e.changedTouches[0].clientY;

    if (Math.abs(deltaX) > 30 && Math.abs(deltaX) > Math.abs(deltaY)) {
      touchLockRef.current = true;
      if (deltaX > 0) {
        next();
      } else {
        prev();
      }
      setTimeout(() => {
        touchLockRef.current = false;
      }, 450);
    }
    touchStartRef.current = null;
  };

  // Visible posts for infinite 2-card laptop view and 1-card mobile view
  const visiblePosts = [
    blogPosts[currentIndex % total],
    blogPosts[(currentIndex + 1) % total],
  ];

  const goTo = useCallback(
    (index: number) => {
      const activeNormalized = currentIndex % total;
      if (index === activeNormalized) return;
      setDirection(index > activeNormalized ? "right" : "left");
      setCurrentIndex(index);
    },
    [currentIndex, total]
  );

  return (
    <main
      id="journal-section"
      className={`${
        isHome ? "pt-0 pb-20" : "pt-28 pb-24 min-h-screen"
      } bg-transparent`}
    >
      <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
        {/* Header Section */}
        <div className="mb-10 md:mb-14">
          <motion.div
            initial={{ y: 40, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="font-Montserrat text-xs tracking-widest uppercase text-indigo-600 dark:text-indigo-300/70 mb-6 flex items-center gap-4"
          >
            <div className="w-4 h-[1px] bg-indigo-500/50" />
            <span>Journal</span>
          </motion.div>

          <motion.h1
            initial={{ y: 40, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{
              duration: 0.8,
              ease: [0.16, 1, 0.3, 1],
              delay: 0.15,
            }}
            className="font-Montserrat text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight text-black dark:text-white leading-[1.05]"
          >
            Thoughts, Insights & <br className="hidden md:block" />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-black dark:from-white via-indigo-600 dark:via-indigo-200 to-indigo-500/50 italic font-serif">
              Visual Culture
            </span>
          </motion.h1>
        </div>

        {/* ── Carousel System with Side Clearance ── */}
        <div className="relative w-full px-0 md:px-14 lg:px-20">
          {/* Left Arrow (Desktop / Tablet) */}
          <div className="hidden md:flex absolute inset-y-0 left-0 items-center z-50 pointer-events-none">
            <button
              onClick={prev}
              type="button"
              className="w-11 h-11 lg:w-14 lg:h-14 rounded-full bg-black/5 dark:bg-white/5 border border-black/10 dark:border-white/10 backdrop-blur-md flex items-center justify-center hover:scale-[1.1] hover:bg-black/10 dark:hover:bg-white/10 active:scale-95 transition-all duration-300 pointer-events-auto cursor-pointer text-black/60 dark:text-white/60 hover:text-black dark:hover:text-white shadow-xl"
              aria-label="Previous story"
            >
              <ArrowLeft className="w-5 h-5 lg:w-6 lg:h-6" strokeWidth={1.5} />
            </button>
          </div>

          {/* Right Arrow (Desktop / Tablet) */}
          <div className="hidden md:flex absolute inset-y-0 right-0 items-center z-50 pointer-events-none">
            <button
              onClick={next}
              type="button"
              className="w-11 h-11 lg:w-14 lg:h-14 rounded-full bg-black/5 dark:bg-white/5 border border-black/10 dark:border-white/10 backdrop-blur-md flex items-center justify-center hover:scale-[1.1] hover:bg-black/10 dark:hover:bg-white/10 active:scale-95 transition-all duration-300 pointer-events-auto cursor-pointer text-black/60 dark:text-white/60 hover:text-black dark:hover:text-white shadow-xl"
              aria-label="Next story"
            >
              <ArrowRight className="w-5 h-5 lg:w-6 lg:h-6" strokeWidth={1.5} />
            </button>
          </div>

          {/* Infinite 2-Card Stage (Desktop) & Swipeable Stage (Mobile) */}
          <div
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            className="w-full touch-pan-y"
          >
            {/* Desktop / Laptop 2-Card Infinite View */}
            <div className="hidden md:grid md:grid-cols-2 gap-6 lg:gap-8 items-stretch">
              <AnimatePresence mode="popLayout" initial={false}>
                {visiblePosts.map((post, index) => (
                  <motion.div
                    key={`${post.slug}-${currentIndex}-${index}`}
                    initial={{
                      opacity: 0,
                      x: direction === "right" ? 30 : -30,
                    }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{
                      opacity: 0,
                      x: direction === "right" ? -30 : 30,
                    }}
                    transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                    className="flex flex-col h-full"
                  >
                    <Link
                      to={`/blog/${post.slug}`}
                      onClick={(e) => {
                        if (isSwipingRef.current) e.preventDefault();
                      }}
                      className="group flex flex-col h-full rounded-3xl overflow-hidden border border-black/10 dark:border-white/10 bg-black/[0.02] dark:bg-white/[0.02] p-6 lg:p-7 transition-all duration-500 hover:shadow-2xl hover:border-indigo-500/40 hover:-translate-y-1.5"
                    >
                      {/* Card Top: Image Container */}
                      <div className="relative w-full aspect-[16/10] rounded-2xl overflow-hidden mb-6 bg-black/5 dark:bg-white/5">
                        <img
                          src={post.image}
                          alt={post.heading}
                          className="w-full h-full object-cover transition-transform duration-[1.2s] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-105"
                        />

                        {/* Top Category Badge */}
                        <div className="absolute top-3.5 left-3.5 flex items-center gap-2">
                          <span className="px-3.5 py-1.5 rounded-full text-[10px] font-Montserrat font-bold uppercase tracking-widest bg-black/70 text-white backdrop-blur-md border border-white/20">
                            {post.category}
                          </span>
                        </div>

                        <div className="absolute bottom-3.5 right-3.5 w-10 h-10 rounded-full bg-white dark:bg-black text-black dark:text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 group-hover:scale-110 shadow-lg">
                          <ArrowUpRight className="w-5 h-5" />
                        </div>
                      </div>

                      {/* Metadata */}
                      <div className="flex items-center gap-3 text-xs font-Montserrat text-black/40 dark:text-white/40 mb-3 uppercase tracking-wider">
                        <span className="flex items-center gap-1.5">
                          <Calendar className="w-3.5 h-3.5" />
                          {post.date}
                        </span>
                        <span>•</span>
                        <span className="flex items-center gap-1.5">
                          <Clock className="w-3.5 h-3.5" />
                          {post.readTime}
                        </span>
                      </div>

                      {/* Title */}
                      <h3 className="font-Montserrat text-xl lg:text-2xl font-bold text-black dark:text-white group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors duration-300 leading-snug mb-3 line-clamp-2">
                        {post.heading}
                      </h3>

                      {/* Excerpt */}
                      <p className="text-black/60 dark:text-white/60 text-xs sm:text-sm md:text-base leading-relaxed line-clamp-3 font-sans mb-6 flex-grow">
                        {post.intro}
                      </p>

                      {/* Bottom CTA */}
                      <div className="pt-4 border-t border-black/10 dark:border-white/10 flex items-center justify-between font-Montserrat text-xs font-bold uppercase tracking-widest text-indigo-600 dark:text-indigo-400 group-hover:gap-2">
                        <span>Read Story</span>
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>

            {/* Mobile View: Synchronized Sliding Swipe Deck */}
            <div className="md:hidden relative w-full overflow-hidden min-h-[460px]">
              <AnimatePresence initial={false} custom={direction}>
                <motion.div
                  key={blogPosts[currentIndex % total].slug}
                  custom={direction}
                  variants={{
                    enter: (dir: "left" | "right") => ({
                      x: dir === "right" ? "105%" : "-105%",
                      opacity: 0.4,
                      scale: 0.94,
                    }),
                    center: {
                      x: 0,
                      opacity: 1,
                      scale: 1,
                    },
                    exit: (dir: "left" | "right") => ({
                      x: dir === "right" ? "-105%" : "105%",
                      opacity: 0.4,
                      scale: 0.94,
                    }),
                  }}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{
                    x: { type: "spring", stiffness: 300, damping: 30 },
                    opacity: { duration: 0.3 },
                    scale: { duration: 0.35 },
                  }}
                  className="w-full absolute inset-0"
                >
                  <Link
                    to={`/blog/${blogPosts[currentIndex % total].slug}`}
                    onClick={(e) => {
                      if (isSwipingRef.current) e.preventDefault();
                    }}
                    className="group flex flex-col h-full rounded-3xl overflow-hidden border border-black/10 dark:border-white/10 bg-black/[0.02] dark:bg-white/[0.02] p-5 sm:p-6 transition-all duration-500 hover:shadow-2xl"
                  >
                    <div className="relative w-full aspect-[16/10] rounded-2xl overflow-hidden mb-5 bg-black/5 dark:bg-white/5">
                      <img
                        src={blogPosts[currentIndex % total].image}
                        alt={blogPosts[currentIndex % total].heading}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute top-3 left-3">
                        <span className="px-3 py-1 rounded-full text-[10px] font-Montserrat font-bold uppercase tracking-widest bg-black/70 text-white backdrop-blur-md border border-white/20">
                          {blogPosts[currentIndex % total].category}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 text-xs font-Montserrat text-black/40 dark:text-white/40 mb-2.5">
                      <span>{blogPosts[currentIndex % total].date}</span>
                      <span>•</span>
                      <span>{blogPosts[currentIndex % total].readTime}</span>
                    </div>

                    <h3 className="font-Montserrat text-xl font-bold text-black dark:text-white mb-3 line-clamp-2">
                      {blogPosts[currentIndex % total].heading}
                    </h3>

                    <p className="text-black/60 dark:text-white/60 text-xs sm:text-sm leading-relaxed line-clamp-3 mb-5">
                      {blogPosts[currentIndex % total].intro}
                    </p>

                    <div className="pt-3 border-t border-black/10 dark:border-white/10 flex items-center justify-between font-Montserrat text-xs font-bold uppercase tracking-widest text-indigo-600 dark:text-indigo-400">
                      <span>Read Story</span>
                      <ArrowRight className="w-4 h-4" />
                    </div>
                  </Link>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>

        {/* ── Interactive Pagination Indicator Dots (Laptop & Mobile) ── */}
        <div className="flex items-center justify-center gap-2.5 mt-6 md:mt-8">
          {blogPosts.map((_, i) => {
            const isActive = currentIndex % total === i;
            return (
              <button
                key={i}
                onClick={() => goTo(i)}
                type="button"
                className={`transition-all duration-300 rounded-full cursor-pointer ${
                  isActive
                    ? "w-8 h-2 bg-indigo-600 dark:bg-indigo-400 shadow-md scale-105"
                    : "w-2 h-2 bg-black/20 dark:bg-white/20 hover:bg-black/40 dark:hover:bg-white/40"
                }`}
                aria-label={`Go to story ${i + 1}`}
              />
            );
          })}
        </div>

        {/* ── Mobile Prompt: Swipe Left / Right Indicator ── */}
        <div className="md:hidden flex items-center justify-center gap-2 text-xs font-Montserrat text-black/50 dark:text-white/50 mt-4 tracking-wider uppercase">
          <MoveHorizontal className="w-3.5 h-3.5 text-indigo-500 animate-pulse" />
          <span>Swipe left or right to explore</span>
        </div>
      </div>
    </main>
  );
}
